import { Avatar } from "@/components/kit";
import { firstName, fromUnits, toUnits } from "@/lib/format";

const U = 10000;
const clean = (u) => String(Number(fromUnits(u, U, 4)));

export function sidePeople(members, kartas, value = {}) {
  return kartas.map((k) => ({
    k,
    people: members
      .filter((m) => m.side_karta_id === k.id && (m.status === "active" || value[m.person_id]))
      .sort((a, b) => (a.role === "karta" ? -1 : b.role === "karta" ? 1 : a.full_name.localeCompare(b.full_name))),
  }));
}

export default function OwnershipEditor({ members, kartas, value, onChange }) {
  const sides = sidePeople(members, kartas, value);
  const units = (pid) => toUnits(value[pid], U);
  const sideTotal = (s) => s.people.reduce((a, p) => a + units(p.person_id), 0);
  const total = sides.reduce((a, s) => a + sideTotal(s), 0);

  const distribute = (owners, tot, next) => {
    const each = Math.floor(tot / owners.length);
    owners.forEach((pid, j) => { next[pid] = clean(j === owners.length - 1 ? tot - each * (owners.length - 1) : each); });
  };

  const applySplit = (pcts) => {
    const next = {};
    sides.forEach((s, i) => {
      let owners = s.people.filter((p) => units(p.person_id) > 0).map((p) => p.person_id);
      if (!owners.length) owners = [s.k.id];
      distribute(owners, pcts[i] * U, next);
    });
    onChange(next);
  };

  const equalWithin = (s) => {
    const owners = s.people.filter((p) => units(p.person_id) > 0).map((p) => p.person_id);
    if (owners.length < 2) return;
    const next = { ...value };
    distribute(owners, sideTotal(s), next);
    onChange(next);
  };

  const ok = total === 100 * U;
  return (
    <div className="space-y-4" data-testid="ownership-editor">
      {kartas.length === 2 && (
        <div>
          <div className="hd-label mb-2">Side split</div>
          <div className="flex gap-2 flex-wrap">
            {[[50, 50], [60, 40], [40, 60], [100, 0], [0, 100]].map((p) => (
              <button type="button" key={p.join("-")} data-testid={`split-preset-${p.join("-")}`} onClick={() => applySplit(p)}
                className="hd-chip bg-white border border-[#E2D9CF] px-3.5 py-2 text-sm hover:border-[#C8553D] transition-colors">
                {p[0]} : {p[1]}
              </button>
            ))}
          </div>
        </div>
      )}
      {sides.map((s) => (
        <div key={s.k.id} className="hd-card" data-testid={`ownership-side-${s.k.id}`}>
          <div className="flex items-center justify-between mb-1">
            <div className="font-bold">{firstName(s.k.full_name)} Side</div>
            <div className="flex items-center gap-2">
              <button type="button" className="text-xs font-semibold text-[#C8553D]" onClick={() => equalWithin(s)} data-testid={`split-equal-${s.k.id}`}>Split equally</button>
              <span className="hd-chip bg-[#F9ECE8] text-[#B34832]" data-testid={`side-total-${s.k.id}`}>{clean(sideTotal(s))}%</span>
            </div>
          </div>
          <div className="text-xs text-[#718096] mb-2">Enter % of the total investment owned by each person</div>
          {s.people.map((p) => (
            <div key={p.person_id} className="flex items-center gap-3 py-2">
              <Avatar name={p.full_name} id={p.person_id} size={34} />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold truncate">{p.full_name}</div>
                <div className="text-[11px] text-[#718096] hs-id">{p.hs_id} · {p.role === "karta" ? "Karta" : p.relation || "Coparcener"}</div>
              </div>
              <div className="relative w-24">
                <input
                  data-testid={`alloc-input-${p.person_id}`}
                  className="hd-input h-11 pr-7 text-right"
                  inputMode="decimal"
                  placeholder="0"
                  value={value[p.person_id] ?? ""}
                  onChange={(e) => onChange({ ...value, [p.person_id]: e.target.value.replace(/[^0-9.]/g, "") })}
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-[#718096]">%</span>
              </div>
            </div>
          ))}
        </div>
      ))}
      <div data-testid="ownership-total" className={`rounded-xl px-4 py-3 text-sm font-semibold ${ok ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-800"}`}>
        {ok ? "Adds up to 100%" : `Total is ${clean(total)}%. ${total < 100 * U ? `Add ${clean(100 * U - total)}%` : `Remove ${clean(total - 100 * U)}%`} to reach 100%.`}
      </div>
    </div>
  );
}
